import { DryingHerbsChartProps } from '../DryingHerbsChart/DryingHerbsChart.component';
import { DryingHerbsForm } from '../DryingHerbsForm/DryingHerbsForm.component';
import { ServiceCommonLayout } from '../ServiceCommonLayout/ServiceCommonLayout.component';
import { DryingHerbsContext } from 'pages/les-outils/assechement-de-l-herbe';
import { DryingHerbs } from 'pleinchamp-api-client';
import React, { ComponentPropsWithRef, FC, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { BecomeProOverlay } from '@components/BecomeProOverlay/BecomeProOverlay.component';
import { FlatButtonVariant } from '@components/buttons/Buttons.type';
import { ChartIcon } from '@components/icons/Chart.icon';
import { Text } from '@components/Text/Text.component';
import { useModal } from '@layout/business/Layout.utils';
import { PlcButtonGroup } from '@layout/components/PlcButtonGroup/PlcButtonGroup.component';
import { useTranslation } from '@locales/useTranslation.hook';
import { Breakpoint, useClassNames, useIsBreakpointDown } from '@styles/breakpoints';
import { userSelectors } from '@user/business/User.store';
import { removeTooltip } from '@utils/chart';
import dynamic from 'next/dynamic';

const DryingHerbsChart = dynamic<DryingHerbsChartProps>(
  () => import('../DryingHerbsChart/DryingHerbsChart.component').then(mod => mod.DryingHerbsChart),
  {
    ssr: false,
  }
);

type Props = ComponentPropsWithRef<'main'>;

const DryingHerbsLayout: FC<Props> = props => {
  const { t } = useTranslation(['services']);
  const {
    city: { data: city, isValidating: isCityValidating },
    news: { data: news, isValidating: isNewsLoading },
  } = useContext(DryingHerbsContext);
  const isPro = useSelector(userSelectors.isPro);
  const [formResults, setFormResults] = useState<DryingHerbs[]>([]);
  const [formError, setFormError] = useState<number>();

  const isMobile = useIsBreakpointDown(Breakpoint.m);
  const modal = useModal();

  const showGraph = formResults.length > 0 && !formError;
  const graph = useMemo(() => {
    return <DryingHerbsChart dryingHerbsData={formResults} />;
  }, [formResults]);

  const graphSectionClassName = useClassNames({ all: 'plc-mt-basis', m: 'plc-mt-xl' });

  const title = useMemo(() => {
    const cityInfos = city && !isCityValidating ? `${city.properties.city} ${city.properties.postcode}` : undefined;
    return `${t('services.dryingHerbs.page-title')}${cityInfos ? ` - ${cityInfos}` : ''}`;
  }, [city, isCityValidating]);

  function openGraph(g?: JSX.Element) {
    modal.show({
      children: g,
      close: removeTooltip,
      rotateOnSmallDevice: true,
      title,
    });
  }
  return (
    <ServiceCommonLayout
      className="drying-herbs"
      i18nServiceBreadcrumb="services.dryingHerbs.page-title"
      i18nServiceSubTitle="services.dryingHerbs.page-subtitle"
      isNewsLoading={isNewsLoading}
      metaTitleI18n="services.dryingHerbs.page-title"
      news={news}
      serviceSlug="assechement-de-l-herbe"
      stringTitle={title}
      {...props}
    >
      {isPro ? (
        <>
          <DryingHerbsForm onFormError={setFormError} onNewResults={setFormResults} />
          <section className={graphSectionClassName}>
            {isMobile ? (
              <PlcButtonGroup
                buttons={[
                  {
                    children: (
                      <>
                        <ChartIcon className="plc-mr-s" />
                        <Text i18nKey="common.see-chart" tag="span" variant="small" />
                      </>
                    ),
                    disabled: !showGraph,
                    onClick: () => openGraph(graph),
                    variant: FlatButtonVariant.secondary,
                  },
                ]}
              />
            ) : (
              showGraph && (
                <>
                  <Text
                    className="plc-container-not-full-width plc-mb-l"
                    i18nKey="services.dryingHerbs.graph.introduction"
                    variant="p"
                  />
                  {graph}
                </>
              )
            )}
          </section>
        </>
      ) : (
        <BecomeProOverlay asOverlay={false} i18nTitle="services.dryingHerbs.become-pro.title" />
      )}
    </ServiceCommonLayout>
  );
};

export { DryingHerbsLayout };
